import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { DateRange } from "@/components/controls/DateRangePicker";
import { DealColumn, DealConfidenceLevel, DealStatus } from "@/pages/deals/types";
import { Filters } from "@/services/api/types";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { ScoreType } from "@/pages/deals/scoreConfig";
import { buildScoreFilterValues } from "@/pages/deals/scoreFilterOptions";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isViewTypeTable } from "@/entities/views/helpers";
import { dealOpenStatuses } from "@/pages/deals/helpers";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { APIFieldItem } from "@/pages/settings/types";
import { pick } from "lodash";
import { ViewType } from "@/entities/views/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import { useProducts } from "@/entities/settings/hooks";
import { useDealStages } from "@/entities/settings/deal/hooks";
import DataTableToolbar, {
  DataTableToolbarImplProps
} from "@/components/controls/DataTableToolbar";
import { NumericCondition } from "@/pages/deals/numericCondition";

export const USER_SORT_BY = "myOrder";

export const getDealsSortByOptions = (arrMrrFieldName: string) => [
  // USER_SORT_BY,
  "name",
  arrMrrFieldName,
  "expectedCloseAt",
  "lastActivity",
  "createdAt"
];

type MRRType = {
  [K in NumericCondition]?: number;
};

type DatePresetOrRange = {
  dateRange: string | DateRange | null;
};

export interface DealFilters extends Filters {
  managedBy?: string[];
  status?: DealStatus[];
  arrMrr?: MRRType | null;
  dealValueAmount?: MRRType | null;
  lastActivity?: DatePresetOrRange;
  expectedCloseAt?: DatePresetOrRange;
  closingQuarter?: DatePresetOrRange;
  createdAt?: DatePresetOrRange;
  closedAt?: DatePresetOrRange;
  closedLostReasonName?: number[];
  aeConfidenceLevel?: DealConfidenceLevel[];
  stage?: number[];
  qualificationText?: string[];
  championsMappingText?: string[];
  stakeholdersApprovalText?: string[];
  keyStakeholdersMappingText?: string[];
  pocText?: string[];
}

// The deal-native TCV and ACV filters, whose keys are the TCV and ACV column
// ids. Those columns show the contract rollups, which are offered in their
// place; saved filters on the deal-native values still render their chips.
export const dealsFilterPickerExcludedKeys: string[] = [
  DealColumn.CONTRACTS_TCV,
  DealColumn.CONTRACTS_ACV
];

interface UseDealsFilterOptionsParams {
  viewType?: ViewType;
  closedLostReasons?: { id: number; name: string }[];
  arrMrrFieldName: string;
}

export const useDealsFilterOptions = ({
  viewType,
  closedLostReasons = [],
  arrMrrFieldName
}: UseDealsFilterOptionsParams): DataTableFilter[] => {
  const { t } = useTranslation();
  const userFilterValues = useUserFilterValues();
  const products = useProducts().data?.products ?? [];
  const dealStages = useDealStages().data?.stages;

  const stageFilterValues = useMemo(
    () =>
      [...(dealStages ?? [])]
        .sort((first, second) => first.stageOrder - second.stageOrder)
        .map((stage) => ({ value: stage.id, label: stage.name })),
    [dealStages]
  );

  const statusEnumType = useMemo(
    () =>
      viewType !== ViewType.DEALS_KANBAN
        ? DealStatus
        : pick(
            DealStatus,
            Object.keys(DealStatus).filter((k) =>
              dealOpenStatuses.includes(
                DealStatus[k as keyof typeof DealStatus]
              )
            )
          ),
    [viewType]
  );

  const dealsFilterOptions: DataTableFilter[] = useMemo(() => {
    const baseFilters: DataTableFilter[] = [
      {
        key: "name",
        filterType: { type: "text" as const }
      },
      {
        key: "companyId",
        columnId: DealColumn.COMPANY_NAME,
        filterType: {
          type: "map" as const,
          values: [],
          sort: true,
          showSearch: true,
          entity: EntityType.COMPANY
        }
      },
      {
        key: "managedBy",
        filterType: {
          type: "map" as const,
          values: userFilterValues,
          showAvatar: true,
          sort: true,
          showSearch: true,
          entity: EntityType.USER
        }
      },
      {
        key: "status",
        excludeNoValue: true,
        filterType: {
          type: "enum" as const,
          values: {
            enumType: statusEnumType,
            entityType: "stakeholders.dealsModal.table",
            valueType: "status",
            getIcon: (status: DealStatus) => (
              <IconContainer
                size="xs"
                style={{ color: dealStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      },
      {
        key: "stage",
        filterType: {
          type: "map" as const,
          values: stageFilterValues,
          showSearch: true
        }
      },
      {
        key: arrMrrFieldName,
        filterType: { type: "numeric" as const, isMonetary: true }
      },
      {
        key: "dealValueAmount",
        filterType: { type: "numeric" as const, isMonetary: true }
      },
      // The TCV and ACV columns show these rollups under their saved-layout
      // ids, so the custom-field mapper matches those columns to the deal-native
      // values instead; these filter what the columns display.
      {
        key: "contractsTotalContractValue",
        columnId: DealColumn.CONTRACTS_TCV,
        filterType: { type: "numeric" as const, isMonetary: true }
      },
      {
        key: "contractsAnnualContractValue",
        columnId: DealColumn.CONTRACTS_ACV,
        filterType: { type: "numeric" as const, isMonetary: true }
      },
      {
        key: "lastActivity",
        filterType: { type: "date" as const }
      },
      {
        key: "expectedCloseAt",
        filterType: { type: "date" as const }
      },
      {
        key: "createdAt",
        filterType: { type: "date" as const }
      },
      {
        key: "aeConfidenceLevel",
        filterType: {
          type: "enum" as const,
          values: {
            enumType: DealConfidenceLevel,
            entityType: "deals",
            valueType: "levels",
            getIcon: (level: DealConfidenceLevel) => (
              <ConfidenceLevelIcon value={level} />
            )
          }
        }
      },
      {
        key: "productId",
        filterType: {
          type: "map" as const,
          values: products.map((product) => ({
            value: product.id,
            label: product.name
          })),
          sort: true,
          showSearch: true
        }
      },
      {
        key: "qualificationText",
        filterType: {
          type: "map" as const,
          values: buildScoreFilterValues(ScoreType.QUALIFICATION, t)
        }
      },
      {
        key: "championsMappingText",
        filterType: {
          type: "map" as const,
          values: buildScoreFilterValues(ScoreType.CHAMPION_SCORE, t)
        }
      },
      {
        key: "stakeholdersApprovalText",
        filterType: {
          type: "map" as const,
          values: buildScoreFilterValues(ScoreType.STAKEHOLDERS_APPROVAL, t)
        }
      },
      {
        key: "keyStakeholdersMappingText",
        filterType: {
          type: "map" as const,
          values: buildScoreFilterValues(ScoreType.STAKEHOLDERS_MAPPING, t)
        }
      },
      {
        key: "pocText",
        filterType: {
          type: "map" as const,
          values: buildScoreFilterValues(ScoreType.POC, t)
        }
      },
      {
        key: DealColumn.NEXT_STEP_TEXT,
        filterType: { type: "text" as const }
      }
    ];

    if (viewType && isViewTypeTable(viewType)) {
      const lastActivityIndex = baseFilters.findIndex(
        (filter) => filter.key === "lastActivity"
      );
      baseFilters.splice(lastActivityIndex, 0, {
        key: "closedAt",
        filterType: { type: "date" as const }
      });
      baseFilters.splice(lastActivityIndex + 1, 0, {
        key: "closedLostReasonName",
        filterType: {
          type: "map" as const,
          values: closedLostReasons.map((reason) => ({
            value: reason.id,
            label: reason.name
          })),
          sort: true
        }
      });
      // Closing quarter is a table column only; the deals request turns this
      // into an expected close date condition (toDealsRequestFilters). Its
      // column is the expected close date's, so Group by offers the date's
      // buckets once rather than buckets of a quarter.
      const expectedCloseIndex = baseFilters.findIndex(
        (filter) => filter.key === "expectedCloseAt"
      );
      baseFilters.splice(expectedCloseIndex + 1, 0, {
        key: DealColumn.CLOSING_QUARTER,
        columnId: DealColumn.EXPECTED_CLOSE_AT,
        filterType: { type: "date" as const }
      });
    }

    return baseFilters;
  }, [
    viewType,
    arrMrrFieldName,
    statusEnumType,
    closedLostReasons,
    userFilterValues,
    products,
    stageFilterValues,
    t
  ]);

  return dealsFilterOptions;
};

interface DealsToolbarProps extends DataTableToolbarImplProps {
  closedLostReasons?: { id: number; name: string }[];
  tableId: string;
  shouldShowEditColumns?: boolean;
  onColumnsChange?: () => void;
  customFields?: APIFieldItem[];
  onViewActivated?: () => void;
}

const DealsToolbar: React.FC<DealsToolbarProps> = (props) => {
  const { viewType, closedLostReasons = [] } = props;
  const { fieldName: arrMrrFieldName } = useArrMrr();

  const dealsSortByOptions = getDealsSortByOptions(arrMrrFieldName);

  const dealsFilterOptions = useDealsFilterOptions({
    viewType,
    closedLostReasons,
    arrMrrFieldName
  });

  return (
    <DataTableToolbar
      filterOptions={dealsFilterOptions}
      sortByOptions={dealsSortByOptions}
      defaultSortByValue={arrMrrFieldName}
      className="px-2"
      actionsClassName="px-0"
      {...props}
    />
  );
};
export default DealsToolbar;
